import React, { useState } from 'react';
import { Download, ArrowRight, FileText, ShieldCheck, RefreshCcw, Mail, Check } from 'lucide-react';
import { Section, Eyebrow } from '../ui/Section';
import { Button } from '../ui/Button';
import { LOGOS } from '../../lib/constants';
const factors = ['Number of users', 'Number and type of devices', 'Support coverage', 'Security requirements', 'Hosting and infrastructure', 'Software and licensing responsibilities', 'Projects or transition work', 'Existing environment complexity'];
const clarityPanels = [{
  title: 'What is assessed',
  body: 'Users, devices, applications, infrastructure, risk and support requirements.'
}, {
  title: 'What is separated',
  body: 'Ongoing managed services, one-off projects and third-party licensing.'
}, {
  title: 'What is confirmed',
  body: 'Scope, responsibilities, timing, inclusions and fees before work begins.'
}];
interface ResourceCard {
  icon: typeof FileText;
  title: string;
  copy: string;
  cta: string;
  accent: string;
}
const resources: ResourceCard[] = [{
  icon: ShieldCheck,
  title: 'Essential Eight Data Pack',
  copy: 'Understand the Essential Eight, the business risks it addresses and the questions a professional firm should ask.',
  cta: 'Download the data pack',
  accent: '#D2223D'
}, {
  icon: RefreshCcw,
  title: 'Switching Providers Guide',
  copy: 'A practical checklist for changing IT providers with clear ownership, mapped dependencies and minimal disruption.',
  cta: 'Download the guide',
  accent: '#0AA77E'
}, {
  icon: FileText,
  title: 'IT Review Explainer',
  copy: 'What an independent IT Review examines, what it should produce and how to use the findings.',
  cta: 'Download the explainer',
  accent: '#008EC6'
}];
function EmailCopy({
  accent
}: {
  accent: string;
}) {
  const [open, setOpen] = useState(false);
  const [sent, setSent] = useState(false);
  const [email, setEmail] = useState('');
  const [first, setFirst] = useState('');
  if (sent) {
    return <p className="mt-3 flex items-center gap-1.5 text-[13px] font-semibold text-managed-deep" data-id="element-410">
        <Check className="h-4 w-4" aria-hidden data-id="element-411" /> We’ll email your copy shortly.
      </p>;
  }
  if (!open) {
    return <button onClick={() => setOpen(true)} className="mt-3 flex items-center gap-1.5 text-[13px] font-semibold text-slate transition-colors hover:text-ink" data-id="element-412">
        <Mail className="h-4 w-4" aria-hidden data-id="element-413" /> Email me a copy
      </button>;
  }
  return <form onSubmit={e => {
    e.preventDefault();
    if (email.trim()) setSent(true);
  }} className="mt-4 space-y-2" data-id="element-414">
      <input type="text" placeholder="" aria-label="First name (optional)" value={first} onChange={e => setFirst(e.target.value)} className="min-h-[42px] w-full rounded-lg border border-border bg-white px-3 text-[14px] outline-none focus:border-ct-cyan" data-id="element-415" />
      <label className="sr-only" htmlFor="email-copy" data-id="element-416">
        Work email
      </label>
      <div className="flex gap-2" data-id="element-417">
        <input id="email-copy" type="email" required placeholder="Work email" value={email} onChange={e => setEmail(e.target.value)} className="min-h-[42px] flex-1 rounded-lg border border-border bg-white px-3 text-[14px] outline-none focus:border-ct-cyan" data-id="element-418" />
        <button type="submit" className="min-h-[42px] rounded-lg px-4 text-[13px] font-bold text-white" style={{
        background: accent
      }} data-id="element-419">
          Send
        </button>
      </div>
    </form>;
}
export function Resources() {
  return <Section id="resources" className="bg-surface-2" ariaLabel="Commercial clarity and resources" data-id="element-420">
      {/* Part A: commercial clarity — equal-height columns, bottoms aligned */}
      <div className="grid grid-cols-1 items-stretch gap-12 lg:grid-cols-2 lg:gap-16" data-id="element-421">
        <div className="flex h-full flex-col" data-id="element-422">
          <Eyebrow data-id="element-423">Honest value starts with clarity</Eyebrow>
          <h2 className="mt-5 font-display text-[30px] font-black leading-[1.08] tracking-tight text-navy md:text-[42px]" data-id="element-424">
            A clear scope before a commitment.
          </h2>
          <p className="mt-5 text-[16px] leading-relaxed text-slate md:text-[17px]" data-id="element-425">
            Managed IT pricing is normally shaped by a handful of practical
            factors:
          </p>
          <ul className="mt-6 grid grid-cols-1 gap-2.5 sm:grid-cols-2" data-id="element-426">
            {factors.map(f => <li key={f} className="flex items-center gap-2.5 rounded-lg bg-white px-3 py-2.5 text-[14px] font-medium text-ink shadow-sm" data-id="element-427">
                <Check className="h-4 w-4 flex-shrink-0 text-ct-deep" aria-hidden data-id="element-428" />
                {f}
              </li>)}
          </ul>
          <div className="mt-auto pt-8" data-id="element-429">
            <Button as="a" href="#it-review" variant="navy" data-id="element-430">
              Discuss the right scope
              <ArrowRight className="h-4 w-4 transition-transform group-hover/btn:translate-x-0.5" aria-hidden data-id="element-431" />
            </Button>
          </div>
        </div>

        <div className="flex h-full flex-col gap-4" data-id="element-432">
          {clarityPanels.map(p => <div key={p.title} className="flex flex-1 flex-col justify-center rounded-card border border-border bg-white p-6 shadow-sm" data-id="element-433">
              <h3 className="font-display text-[16px] font-bold text-navy" data-id="element-434">
                {p.title}
              </h3>
              <p className="mt-1.5 text-[14px] leading-relaxed text-slate" data-id="element-435">
                {p.body}
              </p>
            </div>)}
        </div>
      </div>

      {/* Part B: downloadable resources */}
      <div className="mt-16 border-t border-border pt-14" data-id="element-436">
        <div className="max-w-2xl" data-id="element-437">
          <Eyebrow data-id="element-438">Useful guidance before a sales conversation</Eyebrow>
          <h2 className="mt-4 font-display text-[28px] font-black leading-tight text-navy md:text-[40px]" data-id="element-439">
            Practical resources for considered buyers.
          </h2>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3" data-id="element-440">
          {resources.map(r => <article key={r.title} className="flex flex-col overflow-hidden rounded-card border border-border bg-white transition-transform duration-200 hover:-translate-y-1" data-id="element-441">
              {/* branded cover */}
              <div className="relative flex h-36 items-center justify-center overflow-hidden" style={{
            background: `${r.accent}0F`
          }} data-id="element-442">
                <img src={LOGOS.main} alt="" aria-hidden className="pointer-events-none absolute -right-6 -bottom-4 w-40 opacity-[0.06]" data-id="element-443" />
                <span className="flex h-16 w-16 items-center justify-center rounded-2xl" style={{
              background: `${r.accent}1A`
            }} data-id="element-444">
                  <r.icon className="h-8 w-8" style={{
                color: r.accent
              }} aria-hidden data-id="element-445" />
                </span>
              </div>
              <div className="flex flex-1 flex-col p-6" data-id="element-446">
                <h3 className="font-display text-[18px] font-bold text-navy" data-id="element-447">
                  {r.title}
                </h3>
                <p className="mt-2 flex-1 text-[14px] leading-relaxed text-slate" data-id="element-448">
                  {r.copy}
                </p>
                <a href="#" className="mt-5 inline-flex items-center gap-1.5 text-[14px] font-bold" style={{
              color: r.accent
            }} data-id="element-449">
                  <Download className="h-4 w-4" aria-hidden data-id="element-450" />
                  {r.cta}
                </a>
                <EmailCopy accent={r.accent} data-id="element-451" />
              </div>
            </article>)}
        </div>
      </div>
    </Section>;
}